'use client';

import { 
  ArrowRight, 
  Leaf,
  Users,
  Award,
  ShieldCheck,
  TrendingUp,
  Heart,
  MapPin,
  Sparkles,
  Globe,
  Target,
  CheckCircle,
  Mail,
  Phone,
  Building
} from 'lucide-react';
import { Button } from '@myapp/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';
import Link from 'next/link';

const milestones = [
  { year: '2025', title: '강원푸드핏 플랫폼 런칭', description: '강원도 특산물 전문 플랫폼 서비스 시작' },
  { year: '2025', title: 'AI 레시피 서비스 도입', description: '사용자 맞춤형 AI 레시피 추천 기능 출시' },
  { year: '2025', title: '농가 직거래 시스템 구축', description: '중간 유통업체 없는 직접 거래 시스템 완성' },
  { year: '2025', title: '못난이 농산물 할인 코너 오픈', description: '환경보호와 농가 지원을 위한 특별 코너 운영' }
];

const coreValues = [
  {
    icon: <Leaf className="w-8 h-8 text-green-500" />,
    title: '친환경 농업',
    description: '지속가능한 농업을 통해 환경을 보호하고 건강한 먹거리를 제공합니다.',
    details: ['무농약 재배 지원', '유기농 인증 농가 우대', '친환경 포장재 사용']
  },
  {
    icon: <Users className="w-8 h-8 text-blue-500" />,
    title: '농가 상생',
    description: '농가와의 직거래를 통해 합리적인 가격으로 상생하는 관계를 만듭니다.',
    details: ['중간 유통업체 제거', '농가 수익 증대', '소비자 가격 절약']
  },
  {
    icon: <Award className="w-8 h-8 text-purple-500" />,
    title: '품질 보증',
    description: '엄격한 품질 관리를 통해 최고 품질의 농산물만을 제공합니다.',
    details: ['품질 검증 시스템', '신선도 보장', '배송 품질 관리']
  },
  {
    icon: <Sparkles className="w-8 h-8 text-yellow-500" />,
    title: 'AI 기술 혁신',
    description: 'AI 기술을 활용하여 개인 맞춤형 레시피와 추천 서비스를 제공합니다.',
    details: ['맞춤형 레시피 생성', '계절별 특산물 추천', '영양정보 분석']
  }
];

const teamMembers = [
  {
    name: '김재형',
    position: '대표이사',
    description: '강원푸드핏의 비전을 이끌어가는 리더',
    email: 'kjhk3082@hallym.ac.kr',
    phone: '010-5379-0476',
    expertise: ['비즈니스 전략', '농업 분야 전문가', '지역 발전']
  },
  {
    name: '김성호',
    position: 'CPO',
    description: '제품 전략 수립과 사용자 경험 최적화를 담당',
    email: 'sungho22@hallym.ac.kr',
    phone: '010-9646-2558',
    expertise: ['제품 전략', '사용자 경험', '시장 분석']
  },
  {
    name: '김강민',
    position: '프론트엔드',
    description: '사용자 인터페이스와 웹 애플리케이션 개발을 담당',
    email: 'gangmin@jhlab.co.kr',
    phone: '010-1234-5678',
    expertise: ['React/Next.js', 'TypeScript', 'UI/UX 구현']
  },
  {
    name: '오민우',
    position: '백엔드 개발',
    description: '서버 인프라 및 데이터베이스 아키텍처를 구축',
    email: 'minwoo@jhlab.co.kr',
    phone: '010-8765-4321',
    expertise: ['백엔드 개발', '데이터베이스 설계', '서버 운영']
  }
];

const stats = [
  { label: '등록 농가', value: '150+', icon: <Users className="w-6 h-6 text-green-500" /> },
  { label: '판매 상품', value: '500+', icon: <Leaf className="w-6 h-6 text-blue-500" /> },
  { label: '만족 고객', value: '10,000+', icon: <Heart className="w-6 h-6 text-red-500" /> },
  { label: 'AI 레시피', value: '1,000+', icon: <Sparkles className="w-6 h-6 text-purple-500" /> }
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-white/20 border-white/30 text-white text-lg px-6 py-2">
              <Building className="w-5 h-5 mr-2" />
              JHLAB
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              강원푸드핏 소개
            </h1>
            <p className="text-xl md:text-2xl text-green-50 mb-8 leading-relaxed">
              강원도의 신선한 특산물과 AI 기술이 만나<br />
              건강한 식탁을 만들어가는 혁신적인 플랫폼입니다
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-2">
                    {stat.icon}
                  </div>
                  <div className="text-2xl md:text-3xl font-bold mb-1">{stat.value}</div>
                  <div className="text-green-100 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                우리의 미션과 비전
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                강원푸드핏은 농가와 소비자를 직접 연결하여 신선한 농산물을 합리적인 가격에 제공하고, 
                AI 기술로 개인 맞춤형 서비스를 구현합니다.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
              <Card className="p-8 border-2 border-green-200 hover:shadow-xl transition-all">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mb-4">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-gray-900">미션</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    강원도 농가의 신선한 특산물을 전국 소비자에게 직접 연결하여, 
                    농가의 소득 증대와 소비자의 건강한 식탁을 동시에 실현합니다.
                  </p>
                </CardContent>
              </Card>

              <Card className="p-8 border-2 border-blue-200 hover:shadow-xl transition-all">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-4">
                    <Globe className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-gray-900">비전</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    AI 기술과 농업의 만남으로 지속가능한 농업 생태계를 구축하고, 
                    누구나 쉽게 건강한 요리를 만들 수 있는 세상을 만듭니다.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                핵심 가치
              </h2>
              <p className="text-lg text-gray-600">
                강원푸드핏이 추구하는 4가지 핵심 가치입니다
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {coreValues.map((value, index) => (
                <Card key={index} className="p-6 hover:shadow-xl transition-all group">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 bg-gray-50 group-hover:bg-gray-100 rounded-full flex items-center justify-center transition-colors">
                        {value.icon}
                      </div>
                      <CardTitle className="text-xl text-gray-900">{value.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      {value.description}
                    </p>
                    <ul className="space-y-2">
                      {value.details.map((detail, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Company History */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                회사 연혁
              </h2>
              <p className="text-lg text-gray-600">
                강원푸드핏의 성장 과정을 소개합니다
              </p>
            </div>

            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex items-start gap-6 group">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform">
                      {milestone.year}
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {milestone.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {milestone.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                팀 소개
              </h2>
              <p className="text-lg text-gray-600">
                강원푸드핏을 이끌어가는 전문가들을 만나보세요
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {teamMembers.map((member, index) => (
                <Card key={index} className="p-6 hover:shadow-xl transition-all">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                        {member.name[0]}
                      </div>
                      <div>
                        <CardTitle className="text-xl text-gray-900">{member.name}</CardTitle>
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                          {member.position}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      {member.description}
                    </p>
                    
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4 text-blue-500" />
                        <a href={`mailto:${member.email}`} className="hover:text-blue-600">
                          {member.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4 text-green-500" />
                        <span>{member.phone}</span>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">전문분야</h4>
                      <div className="flex flex-wrap gap-1">
                        {member.expertise.map((skill, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-gray-50 border-gray-200 text-gray-700">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                회사 정보
              </h2>
            </div>

            <Card className="p-8 border-2 border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">기업 정보</h3>
                  <div className="space-y-3 text-gray-700">
                    <div><strong>회사명:</strong> JHLAB</div>
                    <div><strong>대표이사:</strong> 김재형</div>
                    <div><strong>설립일:</strong> 2024년</div>
                    <div><strong>사업분야:</strong> 농산물 전자상거래, AI 서비스</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">연락처</h3>
                  <div className="space-y-3 text-gray-700">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-green-500" />
                      <span>강원특별자치도 춘천시 한림대학길 1 공학관 1130</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-blue-500" />
                      <a href="mailto:kjhk3082@hallym.ac.kr" className="hover:text-blue-600">
                        kjhk3082@hallym.ac.kr
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-green-500" />
                      <span>010-5379-0476</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-green-600 to-blue-600 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            강원푸드핏과 함께하세요
          </h2>
          <p className="text-xl text-green-50 mb-8 max-w-2xl mx-auto">
            신선한 강원도 특산물과 AI 맞춤 레시피로 건강한 식탁을 만들어보세요
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/seasonal">
              <Button size="lg" className="bg-white text-green-600 hover:bg-gray-50 px-8 py-3 text-lg rounded-xl">
                <Leaf className="w-5 h-5 mr-2" />
                제철 특산물 보기
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link href="/recipes">
              <Button size="lg" variant="outline" className="border-white text-green-600 bg-white hover:bg-green-50 hover:text-green-700 px-8 py-3 text-lg rounded-xl">
                <Sparkles className="w-5 h-5 mr-2" />
                AI 레시피 체험
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}